import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import Loading from '../../loading/loadingPage'
import { SEVERITY } from '../makeReady/severity'
import { canManageMakeReady } from '../makeReadyModule/makeReadyAccess'

// The board's Stores tab: one card per kit store with the readiness rollup
// of its contents (GET /kitstores), plus create / edit / archive / bulk
// "add assets" management inline. A store is a location - the drill-in
// reuses the ordinary board cards via the ?store= filter rather than
// inventing new ones. docs/product-specs/make-ready-stores.md.

export const useKitStores = () => {
  const [stores, setStores] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setFetchError(false)
    API.get('/kitstores')
      .then((res) => {
        if (cancelled) return
        if (res.status !== 200) throw new Error('kitstores')
        setStores(res.data ?? [])
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setFetchError(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  return {
    stores,
    loading,
    fetchError,
    refresh: () => setReloadKey((k) => k + 1),
  }
}

const StoreDialog = ({ open, store, onClose, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [name, setName] = useState('')
  const [base, setBase] = useState(null)
  const [notes, setNotes] = useState('')
  const [baseOptions, setBaseOptions] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(store?.name ?? '')
    setBase(store?.baseLocation ?? null)
    setNotes(store?.notes ?? '')
    API.get('/base-location-options')
      .then((res) => res.status === 200 && setBaseOptions(res.data ?? []))
      .catch(() => setBaseOptions([]))
  }, [open, store])

  const handleSave = () => {
    if (!name.trim() || saving) return
    setSaving(true)
    const body = {
      name: name.trim(),
      baseLocation: base?._id ?? null,
      notes: notes.trim(),
    }
    const request = store?._id
      ? API.put(`/kitstores/${store._id}`, body)
      : API.post('/kitstores', body)
    request
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: store?._id ? 'Store updated' : 'Store created' })
          onSaved()
          onClose()
        } else if (res.status === 400) {
          setToast({
            message: 'A store with this name already exists.',
            severity: 'error',
          })
        } else {
          setToast({
            message: 'Unable to save the store. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to save the store. Please try again later.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {store?._id ? `Edit ${store.name}` : 'Add a kit store'}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
          <Autocomplete
            options={baseOptions}
            value={base}
            onChange={(e, value) => setBase(value)}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            getOptionLabel={(b) => b.name ?? ''}
            renderInput={(params) => (
              <TextField {...params} label="Base location" />
            )}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!name.trim() || !base || saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Bulk "put these assets in this store". Options come from the ordinary
// asset search; assigning clears vehicle/parentAsset server-side.
const AddAssetsDialog = ({ open, store, onClose, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [input, setInput] = useState('')
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState([])
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)
  const searchTimer = useRef(null)

  useEffect(() => {
    if (!open) return
    setInput('')
    setOptions([])
    setSelected([])
  }, [open])

  useEffect(() => {
    if (!open) return
    const q = input.trim()
    if (q.length < 2) {
      setOptions([])
      return undefined
    }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setSearching(true)
      API.get(`/assets?q=${encodeURIComponent(q)}`)
        .then(
          (res) =>
            res.status === 200 &&
            setOptions(
              (res.data ?? []).filter((a) => a.store?._id !== store?._id),
            ),
        )
        .catch(() => setOptions([]))
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(searchTimer.current)
  }, [input, open, store])

  const currentPlace = (asset) =>
    asset.vehicle?.callSign
      ? `on ${asset.vehicle.callSign}`
      : asset.parentAsset?.assetID
        ? `in ${asset.parentAsset.assetID}`
        : asset.store?.name
          ? `in ${asset.store.name}`
          : null

  const handleSave = () => {
    if (!selected.length || saving) return
    setSaving(true)
    API.post(`/kitstores/${store._id}/assign`, {
      assetIds: selected.map((a) => a._id),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `${res.data?.updated ?? selected.length} asset(s) moved to ${store.name}`,
          })
          onSaved()
          onClose()
        } else {
          setToast({
            message: 'Unable to move assets. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to move assets. Please try again later.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Add assets to {store?.name}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Anything already on a vehicle or inside a bag is moved out of there
          and onto this store&apos;s shelf.
        </DialogContentText>
        <Autocomplete
          multiple
          options={options}
          loading={searching}
          filterOptions={(x) => x}
          value={selected}
          onChange={(e, value) => setSelected(value)}
          inputValue={input}
          onInputChange={(e, value) => setInput(value)}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          getOptionLabel={(a) => a.assetID ?? ''}
          noOptionsText={
            input.trim().length < 2 ? 'Type to search assets…' : 'No matches'
          }
          renderOption={(props, asset) => (
            <Box component="li" {...props} key={asset._id}>
              <Stack>
                <Typography fontWeight={700}>{asset.assetID}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    [asset.category, asset.subCategory]
                      .filter(Boolean)
                      .join(' · '),
                    currentPlace(asset),
                  ]
                    .filter(Boolean)
                    .join(' — ')}
                </Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Search asset ID / category" autoFocus />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected.length || saving}
          onClick={handleSave}
        >
          {saving ? 'Moving…' : `Move ${selected.length || ''} to store`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const levelChip = (level, count) => (
  <Chip
    key={level}
    size="small"
    label={`${SEVERITY[level].label} (${count})`}
    sx={
      level === 'untracked'
        ? {
            bgcolor: '#F1F5F9',
            color: '#64748B',
            border: `1px dashed ${SEVERITY.untracked.hex}`,
            fontWeight: 600,
          }
        : {
            bgcolor: SEVERITY[level].hex,
            color: '#fff',
            fontWeight: 600,
          }
    }
  />
)

const StoreCard = ({ store, canManage, onOpen, onEdit, onArchive, onAddAssets }) => {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const worst = store.worstLevel ? SEVERITY[store.worstLevel] : null
  const shownLevels = ['expired', 'missing', 'expiringSoon', 'untracked'].filter(
    (level) => (store.levelCounts?.[level] || 0) > 0,
  )

  return (
    <Paper
      sx={{
        p: 2,
        borderLeft: `4px ${store.worstLevel === 'untracked' || !worst ? 'dashed' : 'solid'} ${worst?.hex ?? SEVERITY.untracked.hex}`,
      }}
    >
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
            <WarehouseOutlinedIcon sx={{ color: 'text.secondary' }} />
            <Stack minWidth={0}>
              <Typography fontWeight={700} noWrap title={store.name}>
                {store.name}
              </Typography>
              {store.baseLocation?.name && (
                <Stack direction="row" gap={0.5} alignItems="center">
                  <HomeWorkOutlinedIcon
                    sx={{ fontSize: 14, color: 'text.secondary' }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {store.baseLocation.name}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
          {canManage && (
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {store.assetCount === 0
            ? 'Nothing stored here yet.'
            : `${store.assetCount} item${store.assetCount === 1 ? '' : 's'}${
                store.bagCount ? ` · ${store.bagCount} bag${store.bagCount === 1 ? '' : 's'}` : ''
              }`}
        </Typography>

        {shownLevels.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {shownLevels.map((level) =>
              levelChip(level, store.levelCounts[level]),
            )}
          </Stack>
        )}

        {store.nextExpiryAt && (
          <Typography variant="caption" color="text.secondary">
            Next expiry {new Date(store.nextExpiryAt).toLocaleDateString('en-GB')}
          </Typography>
        )}

        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={store.assetCount === 0}
            onClick={() => onOpen(store)}
          >
            View contents
          </Button>
        </Stack>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            onAddAssets(store)
          }}
        >
          <ListItemIcon>
            <PlaylistAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add assets</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            onEdit(store)
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={store.assetCount > 0}
          onClick={() => {
            setMenuAnchor(null)
            onArchive(store)
          }}
        >
          <ListItemIcon>
            <ArchiveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {store.assetCount > 0 ? 'Archive (empty it first)' : 'Archive'}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  )
}

const StoresPanel = ({ stores, loading, fetchError, refresh, onOpenStore }) => {
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const canManage = useMemo(() => canManageMakeReady(user), [user])
  const [dialog, setDialog] = useState(null) // { kind: 'edit'|'add-assets', store }

  const archiveStore = (store) =>
    API.delete(`/kitstores/${store._id}`)
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: `${store.name} archived` })
          refresh()
        } else {
          setToast({
            message:
              'Unable to archive - move or remove its assets first, then try again.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to archive the store. Please try again later.',
          severity: 'error',
        }),
      )

  if (loading) return <Loading hideText />
  if (fetchError) {
    return (
      <Alert severity="error">
        Unable to load kit stores right now. Please try again later.
      </Alert>
    )
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {stores.length === 0
            ? 'Kit stores are named places kit lives when it isn’t on a vehicle - a store room, a shelf, an event cache.'
            : `${stores.length} store${stores.length === 1 ? '' : 's'}`}
        </Typography>
        {canManage && (
          <Button
            size="small"
            variant="contained"
            onClick={() => setDialog({ kind: 'edit', store: null })}
          >
            Add store
          </Button>
        )}
      </Stack>

      {stores.length === 0 ? (
        <Alert severity="info">
          No kit stores yet. Create one, then shelve bags and assets in it
          from their asset pages or the store&apos;s &ldquo;Add assets&rdquo;
          menu - the board will roll up the readiness of everything inside.
        </Alert>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', xl: '1fr 1fr 1fr' }}
          gap={2}
        >
          {stores.map((store) => (
            <StoreCard
              key={store._id}
              store={store}
              canManage={canManage}
              onOpen={onOpenStore}
              onEdit={(s) => setDialog({ kind: 'edit', store: s })}
              onArchive={archiveStore}
              onAddAssets={(s) => setDialog({ kind: 'add-assets', store: s })}
            />
          ))}
        </Box>
      )}

      <StoreDialog
        open={dialog?.kind === 'edit'}
        store={dialog?.store}
        onClose={() => setDialog(null)}
        onSaved={refresh}
      />
      <AddAssetsDialog
        open={dialog?.kind === 'add-assets'}
        store={dialog?.store}
        onClose={() => setDialog(null)}
        onSaved={refresh}
      />
    </Stack>
  )
}

export default StoresPanel
